import React, { useEffect, useState, useMemo } from "react";
import Decimal from "decimal.js";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader } from "lucide-react";
import { manualPosBaseSchema, getFormManualClientOpenPosSchema, useCreateManualClientPosition, useUpdateManualClientPosition } from "@/lib/api/risk/manualEntries";
import { useListMinimalUsers } from "@/lib/api/users";
import { useListAccounts, formulateOptionMonths } from "@/lib/api/otc";
import { useToast } from "@/components/ui/use-toast";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Combobox } from "@/components/ui/combobox";
import { TypeableCombobox } from "@/components/ui/typableCombobox";
import { CommodityOptions, Commodities } from "@/lib/api/commodity";
import { useListCustomers } from "@/lib/api/customer";
import moment from "moment";
import { SelectOptionItem } from "@/lib/models/ui";
import { Textarea } from "@/components/ui/textarea";
import { useQueryParamArray } from "@/hooks/use-query-param-array";
import { ClientPosition } from "@/lib/api/risk/positions";
import { Expiration } from "@lhc/commodities/dates";
import { getOptionMonthComboBase } from "@/lib/api/common";

type FormData = z.infer<typeof manualPosBaseSchema>;

interface ManualClientPositionFormProps {
    mode: 'create' | 'edit';
    initialData?: ClientPosition;
    onSuccess: () => void;
    onCancel?: () => void;
    showCancelButton?: boolean;
    defaultSourceType?: string;
}

export function ManualClientPositionForm({ mode, initialData, onSuccess, onCancel, showCancelButton = true, defaultSourceType }: ManualClientPositionFormProps) {
    const { toast } = useToast();
    const createManualClientPosition = useCreateManualClientPosition();
    const updateManualClientPosition = useUpdateManualClientPosition();
    const minimalUserListResult = useListMinimalUsers();
    const listAccountsRes = useListAccounts(false);
    const minimalCustomersListResult = useListCustomers({ minimal: true, hasRiskProfile: true });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [customerIds] = useQueryParamArray("customer_ids");

    // Convert ClientPosition data to form data format
    const getInitialFormData = (): Partial<FormData> => {
        if (mode === 'edit' && initialData) {
            return {
                source_type: initialData.source_type || "Open Order - TT",
                contract_number: initialData.original_row?.contract_number || "",
                contract_release: initialData.original_row?.contract_release || "",
                price: initialData.price?.toString() || "",
                commodity: initialData.commodity || "",
                quantity: initialData.bushel?.toString() || "",
                customer_id: initialData.customer_id || "",
                trading_account_id: initialData.trading_account_id || "",
                expiry_date: initialData.expiry_date ? moment(initialData.expiry_date).format("YYYY-MM-DD") : moment().add(1, 'month').format("YYYY-MM-DD"),
                option_month: initialData.option_month || "",
                note: initialData.note || "",
                crop_year: initialData.crop_year || new Date().getFullYear(),
                instrument_type: initialData.instrument_type || (initialData.put_or_call ? "Option" : "Future"),
                put_or_call: initialData.put_or_call || "",
                long_qty: initialData.long_qty?.toString() || "",
                short_qty: initialData.short_qty?.toString() || "",
            };
        }
        
        return {
            source_type: defaultSourceType || "Open Order - TT",
            contract_number: "",
            contract_release: "",
            price: "",
            commodity: "",
            quantity: "",
            customer_id: customerIds?.length > 0 ? customerIds[0] : '',
            trading_account_id: "",
            expiry_date: moment().add(1, 'month').format("YYYY-MM-DD"),
            option_month: "",
            note: "",
            crop_year: new Date().getFullYear(),
            instrument_type: "Future",
            put_or_call: "",
            long_qty: "",
            short_qty: "",
        };
    };

    const [currentSchema, setCurrentSchema] = useState(getFormManualClientOpenPosSchema("Open Order - RJO", "Future"));
    
    const form = useForm<FormData>({
        resolver: zodResolver(currentSchema),
        defaultValues: getInitialFormData(),
    });

    const minimalCustomersList = minimalCustomersListResult?.data?.data || [];
    const users = (minimalUserListResult?.data?.data || []);
    const accounts = (listAccountsRes?.data?.data || []);
    
    // Watch form values for dynamic behavior
    const sourceType = form.watch("source_type");
    const instrumentType = form.watch("instrument_type");
    const commodity = form.watch("commodity");
    const longQuantity = form.watch("long_qty");
    const shortQuantity = form.watch("short_qty");
    const option_month_value = form.watch("option_month");
    const strike_price = form.watch("price");

    // Calculate quantity for Open Orders
    const calculateQuantity = (longQty: string, shortQty: string, commodityType: string) => {
        const longVal = parseFloat(longQty || "0");
        const shortVal = parseFloat(shortQty || "0");
        const totalQty = longVal + shortVal;
        
        if (totalQty === 0) return "";
        
        // Apply multiplier based on commodity
        const quantityMultiplier = commodityType === Commodities.Canola 
            ? new Decimal(20).times(2204.62)
            : new Decimal(5000);
            
        return quantityMultiplier.times(totalQty).toString();
    };
    
    // Helper function to check if field should be visible
    const isFieldVisible = (fieldName: string) => {
        switch (fieldName) {
            case "instrument_type":
                return sourceType === "Open Order - RJO" || sourceType === "Open Order - TT" || sourceType === "RJO";
            case "put_or_call":
                return ((sourceType === "Open Order - RJO" || sourceType === "Open Order - TT" || sourceType === "RJO") && instrumentType === "Option") || sourceType === "OTC";
            case "price":
                return (sourceType === "Open Order - TT" && instrumentType === "px Basis") || ((sourceType === "Open Order - RJO" || sourceType === "Open Order - TT" || sourceType === "RJO") && instrumentType === "Option") || (["Open Order - TT", "RJO"].includes(sourceType) && instrumentType === "Future") || (sourceType === "Open Order - RJO" && instrumentType === "Future") || sourceType === "OTC";
            case "long_qty":
            case "short_qty":
                return sourceType === "Open Order - RJO" || sourceType === "Open Order - TT" || sourceType === "RJO";
            case "expiry_date":
                return sourceType === "OTC" || ((sourceType === "Open Order - RJO" || sourceType === "Open Order - TT" || sourceType === "RJO") && instrumentType === "Option");
            default:
                return true;
        }
    };

    useEffect(() => {
        const unknownAccount = accounts?.find((account: { account_id: string }) => account.account_id === 'Unknown');
        if (!!unknownAccount && !form.watch("trading_account_id")) {
            form.setValue("trading_account_id", unknownAccount.id)
        }
    }, [accounts])
    
    // Update schema when source type or instrument type changes
    useEffect(() => {
        const newSchema = getFormManualClientOpenPosSchema(sourceType || "OTC", instrumentType);
        setCurrentSchema(newSchema);

        if (sourceType !== "Open Order - TT" && instrumentType === "px Basis") {
            form.setValue("instrument_type", "Future");
        }

        // Reset dependent fields when source type changes
        if (sourceType === "OTC") {
            form.setValue("instrument_type", "");
            //form.setValue("put_or_call", "");
            //form.setValue("price", "");
            form.setValue("long_qty", "");
            form.setValue("short_qty", "");
        } else if (sourceType === "Open Order - RJO" || sourceType === "Open Order - TT" || sourceType === "RJO") {
            if (!instrumentType) {
                form.setValue("instrument_type", "Future");
            }
            // Reset option-specific fields when switching to Future
            if (instrumentType === "Future" || instrumentType === 'px Basis') {
                form.setValue("put_or_call", "");
                form.setValue("expiry_date", "");
            }
        }
    }, [sourceType, instrumentType, form]);
    
    // Calculate quantity for Open Orders
    useEffect(() => {
        if (sourceType === "Open Order - RJO" || sourceType === "Open Order - TT" || sourceType === "RJO") {
            const calculatedQuantity = calculateQuantity(longQuantity || "", shortQuantity || "", commodity || "");
            form.setValue("quantity", calculatedQuantity);
        }
    }, [longQuantity, shortQuantity, commodity, sourceType, form]);

    // Calculate expiration date for Options
    useEffect(() => {
        // Only calculate expiration date for Options (not Futures) and when expiry_date field is visible
        if (option_month_value && instrumentType === "Option" && isFieldVisible("expiry_date")) {
            try {
                const optionMonthResult = getOptionMonthComboBase(option_month_value);
                const new_expiration_date = Expiration.calculateLastTradingDay(
                    optionMonthResult.firstDayOfMonthMoment
                );
                // Format the date as YYYY-MM-DD for the date input
                form.setValue("expiry_date", moment(new_expiration_date).format("YYYY-MM-DD"));
            } catch (error) {
                console.error("Error calculating expiration date:", error);
                // Keep the existing value if calculation fails
            }
        }
        // Also handle OTC type (OTC contracts also have expiry dates and option months)
        else if (option_month_value && sourceType === "OTC" && isFieldVisible("expiry_date")) {
            try {
                const optionMonthResult = getOptionMonthComboBase(option_month_value);
                const new_expiration_date = Expiration.calculateLastTradingDay(
                    optionMonthResult.firstDayOfMonthMoment
                );
                // Format the date as YYYY-MM-DD for the date input
                form.setValue("expiry_date", moment(new_expiration_date).format("YYYY-MM-DD"));
            } catch (error) {
                console.error("Error calculating expiration date for OTC:", error);
                // Keep the existing value if calculation fails
            }
        }
    }, [option_month_value, instrumentType, sourceType, form, isFieldVisible]);

    // Create commodity options for combobox
    const commodityOptions: SelectOptionItem<string>[] = CommodityOptions;

    const {
        customerOptions, tradingAccountOptions, optionMonthOptions
    } = useMemo(() => {
        // Create customer options for combobox
        const customerOptions: SelectOptionItem<string>[] = minimalCustomersList.map((user: { long_name: string; id: string; }) => ({
            value: user.id,
            label: user.long_name
        }));

        // Create trading account options for combobox
        const tradingAccountOptions: SelectOptionItem<string>[] = accounts.map((account: { account_id: string; id: string; }) => ({
            value: account.id,
            label: account.account_id
        }));

        // Create option month options using formulateOptionMonths
        const optionMonthOptions: SelectOptionItem<string>[] = formulateOptionMonths(option_month_value);

        return { tradingAccountOptions, customerOptions, optionMonthOptions };
    }, [accounts, minimalCustomersList, option_month_value])

    const openOfferSourceTypeOptions: SelectOptionItem<string>[] = [
        { value: "Open Order - TT", label: "Open Order - TT" },
        { value: "Open Order - RJO", label: "Open Order - RJO" },
        { value: "OTC", label: "OTC" },
    ];

    const brokerageSourceTypeOptions: SelectOptionItem<string>[] = [
        { value: "RJO", label: "RJO" }
    ];

    const allSourceTypeOptions: SelectOptionItem<string>[] = [
        ...openOfferSourceTypeOptions,
        ...brokerageSourceTypeOptions,
    ];

    // Create source type options for combobox
    const sourceTypeOptions: SelectOptionItem<string>[] = mode === 'edit' ? allSourceTypeOptions :  defaultSourceType === "RJO" ? brokerageSourceTypeOptions : openOfferSourceTypeOptions;
    
    // Create instrument type options
    const instrumentTypeOptions: SelectOptionItem<string>[] = [
        ...(sourceType === 'Open Order - TT' ? ([{ value: "px Basis", label: "px Basis" }]) : []),
        ...(sourceType === 'Open Order - TT' ? ([{ value: "Future", label: "HTA" }]) : [{ value: "Future", label: "Future" }]),
        { value: "Option", label: "Option" }
    ];
    
    // Create put/call options
    const putCallOptions: SelectOptionItem<string>[] = [
        { value: "P", label: "Put" },
        { value: "C", label: "Call" }
    ];
    
    // Helper function to check if field is required
    const isFieldRequired = (fieldName: string) => {
        try {
            const schema = getFormManualClientOpenPosSchema(sourceType || "OTC", instrumentType);
            // For ZodEffects (refined schemas), we need to check the underlying schema
            const baseSchema = 'innerType' in schema ? schema.innerType() : schema;
            if ('shape' in baseSchema) {
                const fieldSchema = (baseSchema as any).shape[fieldName];
                return fieldSchema && !fieldSchema.isOptional();
            }
            return false;
        } catch {
            return false;
        }
    };

    const onSubmit = async (data: FormData) => {
        setIsSubmitting(true);
        try {
            // Prepare data for API
            const submitDataResult = getFormManualClientOpenPosSchema(data.source_type, data.instrument_type).safeParse({
                source_type: data.source_type || undefined,
                contract_number: data.contract_number || undefined,
                contract_release: data.contract_release || undefined,
                price: data.price || undefined,
                commodity: data.commodity,
                quantity: data.quantity,
                customer_id: data.customer_id,
                trading_account_id: data.trading_account_id,
                expiry_date: data.expiry_date || undefined,
                option_month: data.option_month?.toLocaleUpperCase() || undefined,
                note: data.note || undefined,
                crop_year: data.crop_year || undefined,

                instrument_type: data.instrument_type,
                put_or_call: data.put_or_call || undefined,
                long_qty: data.long_qty || undefined,
                short_qty: data.short_qty || undefined,
            });

            if (!submitDataResult.success) {
                console.error("Failed to submit manual client position form", submitDataResult.error.errors)
                toast({ title: "Invalid data" });
                return submitDataResult;
            }

            const submitData: z.infer<typeof manualPosBaseSchema> = submitDataResult.data;

            let result;
            if (mode === 'create') {
                result = await createManualClientPosition(submitData);
            } else {
                result = await updateManualClientPosition(initialData!.id, submitData);
            }

            if (!result.success) {
                throw result;
            }

            toast({ 
                title: mode === 'create' 
                    ? "Manual client position created successfully" 
                    : "Manual client position updated successfully" 
            });
            
            if (mode === 'create') {
                form.reset();
            }
            onSuccess();
        } catch (error) {
            console.error(`Failed to ${mode} manual client position`, error);
            toast({
                title: `Failed to ${mode} manual client position`,
                description: error instanceof Error ? error.message : "An unexpected error occurred",
                variant: "destructive"
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                <Card className="shadow-sm">
                    <CardContent className="p-6">
                        {/* Section 1: Source & Product Configuration */}
                        <div className="space-y-4">
                            <div className="flex items-center gap-2">
                                <h3 className="text-lg font-medium text-foreground">Source & Product Configuration</h3>
                                <div className="flex-1 h-px bg-border"></div>
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                                <FormField
                                    control={form.control}
                                    name="source_type"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel className="text-sm font-medium">
                                                Source Type{isFieldRequired("source_type") && <span className="text-destructive ml-1">*</span>}
                                            </FormLabel>
                                            <FormControl>
                                                <Combobox
                                                    dwimOnChange
                                                    isModal
                                                    options={sourceTypeOptions}
                                                    value={field.value || ""}
                                                    onValueChange={field.onChange}
                                                    placeholder="Select source type"
                                                    className="w-full"
                                                    popoverClassName="w-full"
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                                
                                {isFieldVisible("instrument_type") && (
                                    <FormField
                                        control={form.control}
                                        name="instrument_type"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel className="text-sm font-medium">
                                                    Instrument Type{isFieldRequired("instrument_type") && <span className="text-destructive ml-1">*</span>}
                                                </FormLabel>
                                                <FormControl>
                                                    <Combobox
                                                        dwimOnChange
                                                        isModal
                                                        options={instrumentTypeOptions}
                                                        value={field.value || ""}
                                                        onValueChange={field.onChange}
                                                        placeholder="Select instrument type"
                                                        className="w-full"
                                                        popoverClassName="w-full"
                                                    />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                )}

                                <FormField
                                    control={form.control}
                                    name="commodity"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel className="text-sm font-medium">
                                                Commodity{isFieldRequired("commodity") && <span className="text-destructive ml-1">*</span>}
                                            </FormLabel>
                                            <FormControl>
                                                <Combobox
                                                    dwimOnChange
                                                    isModal
                                                    options={commodityOptions}
                                                    value={field.value || ""}
                                                    onValueChange={field.onChange}
                                                    placeholder="Select a commodity"
                                                    className="w-full"
                                                    popoverClassName="w-full"
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />

                                <FormField
                                    control={form.control}
                                    name="option_month"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel className="text-sm font-medium">
                                                {(sourceType === "Open Order - RJO" || sourceType === "Open Order - TT" || sourceType === "RJO") && instrumentType === "Future" 
                                                    ? "Future Month" 
                                                    : "Option Month"}
                                                {isFieldRequired("option_month") && <span className="text-destructive ml-1">*</span>}
                                            </FormLabel>
                                            <FormControl>
                                                <TypeableCombobox
                                                    dwimOnChange
                                                    isModal
                                                    options={optionMonthOptions}
                                                    value={field.value || ""}
                                                    onValueChange={field.onChange}
                                                    placeholder=""
                                                    className="w-full"
                                                    popoverClassName="w-full"
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                                
                                {isFieldVisible("put_or_call") && (
                                    <FormField
                                        control={form.control}
                                        name="put_or_call"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel className="text-sm font-medium">
                                                    Put/Call{isFieldRequired("put_or_call") && <span className="text-destructive ml-1">*</span>}
                                                </FormLabel>
                                                <FormControl>
                                                    <Combobox
                                                        dwimOnChange
                                                        isModal
                                                        options={putCallOptions}
                                                        value={field.value || ""}
                                                        onValueChange={field.onChange}
                                                        placeholder="Select Put or Call"
                                                        className="w-full"
                                                        popoverClassName="w-full"
                                                    />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                )}
                                
                                {isFieldVisible("expiry_date") && (
                                    <FormField
                                        control={form.control}
                                        name="expiry_date"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel className="text-sm font-medium">
                                                    Expiry Date{isFieldRequired("expiry_date") && <span className="text-destructive ml-1">*</span>}
                                                </FormLabel>
                                                <FormControl>
                                                    <Input type="date" {...field} className="transition-colors" />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                )}
                            </div>
                        </div>

                        <Separator className="my-6" />

                        {/* Section 2: Quantities & Pricing */}
                        <div className="space-y-4">
                            <div className="flex items-center gap-2">
                                <h3 className="text-lg font-medium text-foreground">Quantities & Pricing</h3>
                                <div className="flex-1 h-px bg-border"></div>
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                                {isFieldVisible("long_qty") && (
                                    <FormField
                                        control={form.control}
                                        name="long_qty"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel className="text-sm font-medium">
                                                    Long Quantity{isFieldRequired("long_qty") && <span className="text-destructive ml-1">*</span>}
                                                </FormLabel>
                                                <FormControl>
                                                    <Input
                                                        {...field}
                                                        placeholder="Enter long quantity"
                                                        type="number"
                                                        step="1"
                                                        className="transition-colors"
                                                    />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                )}
                                
                                {isFieldVisible("short_qty") && (
                                    <FormField
                                        control={form.control}
                                        name="short_qty"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel className="text-sm font-medium">
                                                    Short Quantity{isFieldRequired("short_qty") && <span className="text-destructive ml-1">*</span>}
                                                </FormLabel>
                                                <FormControl>
                                                    <Input
                                                        {...field}
                                                        placeholder="Enter short quantity"
                                                        type="number"
                                                        step="1"
                                                        className="transition-colors"
                                                    />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                )}
                                
                                <FormField
                                    control={form.control}
                                    name="quantity"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel className="text-sm font-medium">
                                                Quantity{isFieldRequired("quantity") && <span className="text-destructive ml-1">*</span>}
                                            </FormLabel>
                                            <FormControl>
                                                <Input
                                                    {...field}
                                                    placeholder={sourceType === "OTC" ? "Enter quantity" : "Calculated automatically"}
                                                    type="number"
                                                    step="1"
                                                    disabled={sourceType === "Open Order - RJO" || sourceType === "Open Order - TT" || sourceType === "RJO"}
                                                    className={`transition-colors ${
                                                        sourceType === "Open Order - RJO" || sourceType === "Open Order - TT" || sourceType === "RJO"
                                                            ? "bg-muted/50 text-muted-foreground" 
                                                            : ""
                                                    }`}
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />

                                <FormField
                                    control={form.control}
                                    name="crop_year"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel className="text-sm font-medium">
                                                Crop Year{isFieldRequired("crop_year") && <span className="text-destructive ml-1">*</span>}
                                            </FormLabel>
                                            <FormControl>
                                                <Input
                                                    type="number"
                                                    {...field}
                                                    onChange={(e) => field.onChange(parseInt(e.target.value) || 0)}
                                                    className="transition-colors"
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />

                                {isFieldVisible("price") && (
                                    <FormField
                                        control={form.control}
                                        name="price"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel className="text-sm font-medium">
                                                    Strike Price{isFieldRequired("price") && <span className="text-destructive ml-1">*</span>}
                                                </FormLabel>
                                                <FormControl>
                                                    <Input
                                                        {...field}
                                                        placeholder="Enter price"
                                                        type="number"
                                                        step="0.01"
                                                        className="transition-colors"
                                                    />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                )}
                            </div>
                        </div>

                        <Separator className="my-6" />

                        {/* Section 4: Contract Details */}
                        <div className="space-y-4">
                            <div className="flex items-center gap-2">
                                <h3 className="text-lg font-medium text-foreground">Contract Details</h3>
                                <div className="flex-1 h-px bg-border"></div>
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                                <FormField
                                    control={form.control}
                                    name="customer_id"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel className="text-sm font-medium">
                                                Customer{isFieldRequired("customer_id") && <span className="text-destructive ml-1">*</span>}
                                            </FormLabel>
                                            <FormControl>
                                                <Combobox
                                                    dwimOnChange
                                                    isModal
                                                    options={customerOptions}
                                                    value={field.value || ""}
                                                    onValueChange={field.onChange}
                                                    placeholder="Select customer"
                                                    className="w-full"
                                                    popoverClassName="w-full min-w-[300px]"
                                                    disabled={minimalUserListResult.isLoading}
                                                    virtualizeList={true}
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />

                                <FormField
                                    control={form.control}
                                    name="contract_number"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel className="text-sm font-medium">
                                                Contract Number{isFieldRequired("contract_number") && <span className="text-destructive ml-1">*</span>}
                                            </FormLabel>
                                            <FormControl>
                                                <Input {...field} placeholder="Enter contract number" className="transition-colors" />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />

                                <FormField
                                    control={form.control}
                                    name="contract_release"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel className="text-sm font-medium">
                                                Contract Release{isFieldRequired("contract_release") && <span className="text-destructive ml-1">*</span>}
                                            </FormLabel>
                                            <FormControl>
                                                <Input {...field} placeholder="Enter contract release" className="transition-colors" />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />

                                <FormField
                                    control={form.control}
                                    name="trading_account_id"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel className="text-sm font-medium">
                                                Trading Account{isFieldRequired("trading_account_id") && <span className="text-destructive ml-1">*</span>}
                                            </FormLabel>
                                            <FormControl>
                                                <Combobox
                                                    dwimOnChange
                                                    isModal
                                                    options={tradingAccountOptions}
                                                    value={field.value || ""}
                                                    onValueChange={field.onChange}
                                                    placeholder="Select trading account"
                                                    className="w-full"
                                                    popoverClassName="w-full min-w-[300px]"
                                                    disabled={listAccountsRes.isLoading}
                                                    virtualizeList={true}
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            </div>
                        </div>

                        <Separator className="my-6" />

                        {/* Section 5: Additional Information */}
                        <div className="space-y-4">
                            <div className="flex items-center gap-2">
                                <h3 className="text-lg font-medium text-foreground">Additional Information</h3>
                                <div className="flex-1 h-px bg-border"></div>
                            </div>
                            
                            <FormField
                                control={form.control}
                                name="note"
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel className="text-sm font-medium">Note</FormLabel>
                                        <FormControl>
                                            <Textarea 
                                                {...field} 
                                                placeholder="Enter any additional notes (optional)"
                                                className="resize-none transition-colors min-h-[80px]"
                                                rows={3}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </div>
                    </CardContent>
                </Card>

                {/* Action Buttons */}
                <div className="flex justify-end gap-3 pt-2">
                    {showCancelButton && onCancel && (
                        <Button 
                            type="button" 
                            variant="outline" 
                            onClick={onCancel} 
                            disabled={isSubmitting}
                            className="min-w-[100px] transition-colors"
                        >
                            Cancel
                        </Button>
                    )}
                    <Button 
                        type="submit" 
                        disabled={isSubmitting}
                        className="min-w-[160px] transition-colors"
                    >
                        {isSubmitting ? (
                            <>
                                <Loader className="h-4 w-4 mr-2 animate-spin" />
                                {mode === 'create' ? 'Creating...' : 'Updating...'}
                            </>
                        ) : (
                            mode === 'create' ? "Create Client Position" : "Update Client Position"
                        )}
                    </Button>
                </div>
            </form>
        </Form>
    );
}
